import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'ember.adminToken';

/**
 * Hook qui gere le token admin en sessionStorage (= persiste pendant la
 * session navigateur, mais perdu a la fermeture de l'onglet, contrairement
 * a localStorage).
 *
 * Choix sessionStorage volontaire : un token admin qui survit a la fermeture
 * du navigateur est plus risque si le device est partage. Mieux vaut re-saisir
 * a chaque session.
 */
export function useAdminToken(): {
  token: string | null;
  setToken: (value: string) => void;
  clearToken: () => void;
} {
  const [token, setTokenState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(STORAGE_KEY);
  });

  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === STORAGE_KEY) {
        setTokenState(event.newValue);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setToken = useCallback((value: string) => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(STORAGE_KEY, value);
    setTokenState(value);
  }, []);

  const clearToken = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(STORAGE_KEY);
    setTokenState(null);
  }, []);

  return { token, setToken, clearToken };
}
